import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { riderApi } from '@/api/rider.api';
import type { RiderProfile } from '@/api/types';
import { useAuth } from '@/auth/auth-context';
import { ApiError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

/**
 * The signed-in rider's own profile, or `null` if they have not been onboarded.
 *
 * The null is a REAL state, not a missing value: the platform grants the RIDER
 * role and creates the `riders` row in two separate steps in the admin panel,
 * and somebody caught between them can sign in perfectly well and has no
 * profile at all. Rendering that as an empty run sheet would send them looking
 * for jobs that were never going to arrive, so it is named on screen instead.
 *
 * An actual failure — the backend unreachable — still surfaces as an error,
 * because "we could not ask" and "the answer is no" want different screens.
 */
export function useRiderProfile() {
  const { isSignedIn } = useAuth();

  return useQuery<RiderProfile | null>({
    queryKey: queryKeys.riderProfile,
    enabled: isSignedIn,
    queryFn: async () => {
      try {
        return await riderApi.me();
      } catch (error) {
        // 404 is "you are not a rider", which is data a screen draws. Anything
        // else — a 500, a timeout — is a failure and is rethrown so it lands
        // as an error rather than as a wrong answer.
        if (error instanceof ApiError && error.isNotFound) return null;
        throw error;
      }
    },
    staleTime: 30_000,
  });
}

/**
 * The shift switch.
 *
 * Optimism is deliberately absent: going on shift is what makes dispatch start
 * handing this phone real deliveries, so the switch does not flip until the
 * server says it has. A rider who saw "On shift" because their browser assumed
 * so would sit waiting for work nobody was sending.
 */
export function useSetOnShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ riderId, onShift }: { riderId: string; onShift: boolean }) =>
      riderApi.setOnShift(riderId, onShift),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.riderProfile, updated);
    },
  });
}

/**
 * Cash the office believes this rider is holding.
 *
 * Refetched on mount rather than cached hard: it changes on every COD handover
 * the rider makes, and a stale figure is the one number here that would start
 * an argument at the end of a shift.
 */
export function useCashBalance() {
  const { isSignedIn } = useAuth();

  return useQuery({
    queryKey: queryKeys.cashBalance,
    enabled: isSignedIn,
    queryFn: () => riderApi.cashBalance(),
    staleTime: 0,
  });
}
